import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/landing/Logo";
import { cms } from "@/lib/cms";
import {
  adminListAuthors, adminCreateAuthor, adminUpdateAuthor, adminDeleteAuthor, absoluteUrl,
} from "@/lib/blog";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Loader2, ImageIcon, Save, User } from "lucide-react";

const EMPTY_DRAFT = { name: "", bio: "", role: "Author", avatar_url: "", twitter: "", linkedin: "", url: "" };

export default function AdminBlogAuthors() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ ...EMPTY_DRAFT });
  const [editing, setEditing] = useState(null); // author id being edited inline
  const avatarInputRef = useRef(null);
  const editAvatarInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try { setItems(await adminListAuthors()); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!draft.name.trim()) return;
    try {
      await adminCreateAuthor(draft);
      setDraft({ ...EMPTY_DRAFT });
      toast.success("Author created");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create author");
    }
  };

  const onPatch = (id, patch) => setItems((prev) => prev.map((a) => a.id === id ? { ...a, ...patch, _dirty: true } : a));

  const saveOne = async (a) => {
    try {
      await adminUpdateAuthor(a.id, {
        name: a.name, slug: a.slug, bio: a.bio, avatar_url: a.avatar_url,
        twitter: a.twitter, linkedin: a.linkedin, role: a.role, url: a.url,
      });
      toast.success("Author saved");
      setEditing(null);
      load();
    } catch (err) {
      toast.error("Save failed");
    }
  };

  const delOne = async (a) => {
    if (!window.confirm(`Delete author "${a.name}"? Posts by this author will have no author.`)) return;
    await adminDeleteAuthor(a.id);
    toast.success("Deleted");
    load();
  };

  const uploadAvatar = async (file, target) => {
    if (!file) return;
    try {
      const r = await cms.upload(file);
      if (target === "draft") {
        setDraft((d) => ({ ...d, avatar_url: r.url }));
      } else {
        onPatch(target, { avatar_url: r.url });
      }
      toast.success("Avatar uploaded");
    } catch {
      toast.error("Upload failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="admin-blog-authors">
      <header className="bg-white/80 backdrop-blur-xl border-b border-slate-200/70 sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-6 lg:px-8 h-16 flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => nav("/admin/blog")} className="rounded-full">
            <ArrowLeft className="h-4 w-4 mr-1.5" />Blog
          </Button>
          <Logo />
          <span className="inline-flex items-center text-[11px] font-bold tracking-wider uppercase px-2 py-1 rounded-full bg-slate-900 text-white">
            Authors
          </span>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 lg:px-8 py-8 space-y-6">
        {/* Create form */}
        <section className="rounded-2xl bg-white border border-slate-200 p-5">
          <h2 className="font-bold text-slate-900 mb-4">Add author</h2>
          <form onSubmit={create} className="space-y-4" data-testid="author-create-form">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Name *</Label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Full name" required data-testid="author-name" />
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Input value={draft.role} onChange={(e) => setDraft({ ...draft, role: e.target.value })} placeholder="e.g. Content Writer" data-testid="author-role" />
              </div>
            </div>

            <div>
              <Label className="text-xs">Bio</Label>
              <Textarea value={draft.bio} onChange={(e) => setDraft({ ...draft, bio: e.target.value })} placeholder="Short bio about the author" rows={2} data-testid="author-bio" />
            </div>

            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Twitter</Label>
                <Input value={draft.twitter} onChange={(e) => setDraft({ ...draft, twitter: e.target.value })} placeholder="@handle" data-testid="author-twitter" />
              </div>
              <div>
                <Label className="text-xs">LinkedIn</Label>
                <Input value={draft.linkedin} onChange={(e) => setDraft({ ...draft, linkedin: e.target.value })} placeholder="Profile URL" data-testid="author-linkedin" />
              </div>
              <div>
                <Label className="text-xs">Website URL</Label>
                <Input value={draft.url} onChange={(e) => setDraft({ ...draft, url: e.target.value })} placeholder="https://..." data-testid="author-url" />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <input ref={avatarInputRef} type="file" accept="image/*" hidden onChange={(e) => uploadAvatar(e.target.files?.[0], "draft")} />
              <Button type="button" variant="outline" size="sm" onClick={() => avatarInputRef.current?.click()} data-testid="author-avatar-upload">
                <ImageIcon className="h-3.5 w-3.5 mr-1.5" />Upload Avatar
              </Button>
              {draft.avatar_url && (
                <div className="flex items-center gap-2">
                  <img src={absoluteUrl(draft.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200" />
                  <button type="button" onClick={() => setDraft({ ...draft, avatar_url: "" })} className="text-xs text-rose-600 font-semibold">Remove</button>
                </div>
              )}
            </div>

            <Button type="submit" className="rounded-full bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="author-create-submit">
              <Plus className="h-4 w-4 mr-1.5" />Add Author
            </Button>
          </form>
        </section>

        {/* Authors list */}
        <section className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="py-10 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-emerald-500" /></div>
          ) : items.length === 0 ? (
            <div className="py-10 text-center text-slate-500 text-sm">No authors yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100" data-testid="author-list">
              {items.map((a) => (
                <li key={a.id} className="p-4" data-testid={`author-row-${a.slug}`}>
                  {editing === a.id ? (
                    /* Expanded edit mode */
                    <div className="space-y-3">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Name</Label>
                          <Input value={a.name} onChange={(e) => onPatch(a.id, { name: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Slug</Label>
                          <Input value={a.slug} onChange={(e) => onPatch(a.id, { slug: e.target.value })} />
                        </div>
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Role</Label>
                          <Input value={a.role || ""} onChange={(e) => onPatch(a.id, { role: e.target.value })} />
                        </div>
                        <div>
                          <Label className="text-xs">Website URL</Label>
                          <Input value={a.url || ""} onChange={(e) => onPatch(a.id, { url: e.target.value })} />
                        </div>
                      </div>
                      <div>
                        <Label className="text-xs">Bio</Label>
                        <Textarea value={a.bio || ""} onChange={(e) => onPatch(a.id, { bio: e.target.value })} rows={2} />
                      </div>
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Twitter</Label>
                          <Input value={a.twitter || ""} onChange={(e) => onPatch(a.id, { twitter: e.target.value })} placeholder="@handle" />
                        </div>
                        <div>
                          <Label className="text-xs">LinkedIn</Label>
                          <Input value={a.linkedin || ""} onChange={(e) => onPatch(a.id, { linkedin: e.target.value })} placeholder="Profile URL" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <input ref={editAvatarInputRef} type="file" accept="image/*" hidden onChange={(e) => uploadAvatar(e.target.files?.[0], a.id)} />
                        <Button type="button" variant="outline" size="sm" onClick={() => editAvatarInputRef.current?.click()}>
                          <ImageIcon className="h-3.5 w-3.5 mr-1.5" />Change Avatar
                        </Button>
                        {a.avatar_url && (
                          <div className="flex items-center gap-2">
                            <img src={absoluteUrl(a.avatar_url)} alt="" className="h-8 w-8 rounded-full object-cover ring-1 ring-slate-200" />
                            <button type="button" onClick={() => onPatch(a.id, { avatar_url: "" })} className="text-xs text-rose-600 font-semibold">Remove</button>
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 justify-end">
                        <Button size="sm" variant="outline" onClick={() => { setEditing(null); load(); }}>Cancel</Button>
                        <Button size="sm" onClick={() => saveOne(a)} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid={`author-save-${a.slug}`}>
                          <Save className="h-3.5 w-3.5 mr-1" />Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    /* Collapsed row */
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-slate-100 ring-1 ring-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                        {a.avatar_url ? (
                          <img src={absoluteUrl(a.avatar_url)} alt={a.name} className="h-full w-full object-cover" />
                        ) : (
                          <User className="h-4 w-4 text-slate-400" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-slate-900 truncate">{a.name}</div>
                        <div className="text-xs text-slate-500">{a.role || "Author"} · <span className="font-mono">/{a.slug}</span></div>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" onClick={() => setEditing(a.id)} data-testid={`author-edit-${a.slug}`}>Edit</Button>
                        <Button size="sm" variant="ghost" onClick={() => delOne(a)} className="text-rose-600" data-testid={`author-delete-${a.slug}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
